/**
 * CDNImage Component
 * Wrapper for Next.js Image component that handles CDN URLs properly
 * Automatically provides default dimensions when not specified
 */

import Image, { ImageProps } from "next/image";
import { StaticImageData } from "next/image";

interface CDNImageProps extends Omit<ImageProps, "src" | "width" | "height"> {
  src: string | StaticImageData;
  alt: string;
  priority?: boolean;
  quality?: number;
  sizes?: string;
  width?: number;
  height?: number;
  fill?: boolean;
  className?: string;
}

export default function CDNImage({
  src,
  alt,
  priority = false,
  quality = 75,
  sizes,
  className = "",
  width,
  height,
  fill,
  ...props
}: CDNImageProps) {
  // Check if src is a CDN URL (string)
  const isCDNUrl = typeof src === "string";

  // If no dimensions provided and not using fill, provide defaults
  const finalWidth = width || (fill ? undefined : 1200);
  const finalHeight = height || (fill ? undefined : 800);
  const shouldUseUnoptimized = isCDNUrl && !width && !height && !fill;

  return (
    <Image
      src={src}
      alt={alt}
      priority={priority}
      loading={priority ? "eager" : "lazy"}
      quality={quality}
      sizes={sizes || "100vw"}
      placeholder={typeof src !== "string" ? "blur" : undefined}
      className={className}
      unoptimized={shouldUseUnoptimized}
      width={finalWidth}
      height={finalHeight}
      fill={fill}
      {...props}
    />
  );
}

/**
 * Usage Examples:
 *
 * // With explicit dimensions (optimized)
 * <CDNImage
 *   src="https://cdn.example.com/image.jpg"
 *   alt="Product"
 *   width={800}
 *   height={600}
 * />
 *
 * // With fill prop (optimized, requires relative parent)
 * <div className="relative w-full h-64">
 *   <CDNImage
 *     src="https://cdn.example.com/image.jpg"
 *     alt="Background"
 *     fill
 *     className="object-cover"
 *   />
 * </div>
 *
 * // Without dimensions (uses defaults: 1200x800, unoptimized)
 * <CDNImage
 *   src="https://cdn.example.com/image.jpg"
 *   alt="Image"
 *   className="w-full h-auto"
 * />
 *
 * // With StaticImageData (always optimized)
 * <CDNImage
 *   src={importedImage}
 *   alt="Static Image"
 * />
 */
